import lodash from 'lodash'
import { getAllRegions } from '@/api/global/request'
import { getRegionEnvsList } from '@/api/settings/request'
import types from '@/store/mutation-types'
import { FEATURE_NAMES } from '@/utils/consts'
import EventBus from '@/utils/eventBus'
import { isLanguageFeature } from '@/utils/i18n'

function someFeatureFn (name) {
  return function (item) {
    return item.name === name
  }
}

function getDefaultBackEndRegionalEnv () {
  return lodash.cloneDeep(defaultState.backEndRegionalEnv)
}

export function buildBackEndRegionalEnvState (regionEnvCodes, envValues = {}) {
  return [].concat(regionEnvCodes).reduce((result, envCode) => {
    const defaultValue = lodash.cloneDeep(defaultState.backEndRegionalEnv[envCode])
    result[envCode] = Object.prototype.hasOwnProperty.call(envValues, envCode)
      ? envValues[envCode]
      : defaultValue
    return result
  }, {})
}

const defaultState = {
  currentRegion: {
    features: [],
    domain: '',
  },
  regions: {},
  activeRegions: [],
  publicPathLastSelectedRegion: {
    features: [],
    domain: '',
  },
  backEndRegionalEnv: {
    'moduleSwitch.holidayWorkingHours': undefined,
    'urls.workPermitQRLink': null,
    'moduleSwitch.submissionInvoiceModule': undefined,
    'moduleSwitch.hoistingModule': undefined,
    'workPermit.sawpDescWorkEventMandatory': undefined,
  },
  isCrossRegionLogin: false,
}

// getters
const getters = {
  currentRegion: state => state.currentRegion,
  allRegions: state => state.regions,
  backEndRegionalEnv: state => state.backEndRegionalEnv,
  activeRegions: state => state.activeRegions,
  publicPathLastSelectedRegion: state => state.publicPathLastSelectedRegion,
  featureStatus: state => {
    let result = {}
    Object.keys(FEATURE_NAMES).forEach(key => {
      result[key] = state.currentRegion.features.some(item => item.name === key)
    })
    return result
  },
  regionName: state => state.currentRegion.name,
  regionLanguageMap: state => {
    const map = {}
    Object.entries(state.regions).forEach(entry => {
      map[entry[0]] = lodash.get(entry[1], 'features', []).filter(feature => isLanguageFeature(feature.name))
    })
    return map
  },
  enableTenderFeature: state => {
    return state.currentRegion.features.some(someFeatureFn(FEATURE_NAMES.TENDERS))
  },
  enableProjectFeature: state => {
    return state.currentRegion.features.some(someFeatureFn(FEATURE_NAMES.HONGKONG_LAND_PROJECTS))
  },
  enableVendorPerformance: state => {
    return state.currentRegion.features.some(someFeatureFn(FEATURE_NAMES.VENDOR_PERFORMANCE))
  },
  enableTenancyRecordsFeature: state => {
    return state.currentRegion.features.some(someFeatureFn(FEATURE_NAMES.TENANCY_RECORDS))
  },
  enableTenantProjectsFeature: state => {
    return state.currentRegion.features.some(someFeatureFn(FEATURE_NAMES.TENANT_PROJECTS))
  },
  enableEcircularFeature: state => {
    return state.currentRegion.features.some(someFeatureFn(FEATURE_NAMES.E_CIRCULAR))
  },
  enableDirectoryFeature: state => {
    return state.currentRegion.features.some(someFeatureFn(FEATURE_NAMES.DIRECTORY))
  },
  enableWorkPermitsFeature: state => {
    return state.currentRegion.features.some(someFeatureFn(FEATURE_NAMES.WORK_PERMITS))
  },
  enableGreenOperationFeature: state => {
    return state.currentRegion.features.some(someFeatureFn(FEATURE_NAMES.GREEN_OPERATION))
  },
  enableGreenFitOutFeature: state => {
    return state.currentRegion.features.some(someFeatureFn(FEATURE_NAMES.GREEN_FIT_OUT))
  },
  isCrossRegionLogin: state => state.isCrossRegionLogin,
}

/**
 * State Actions
 */
const actions = {
  updateCurrentRegion ({state, commit}, regionName) {
    const result = lodash.get(state.regions, regionName)
    if (!result) {
      console.error('The target region is invalid, region: ', regionName)
      return
    }
    commit(types.CURRENT_REGION, result)
    EventBus.$emit('updated_current_region')
  },
  async initRegionData ({state, commit}, forceLoad = false) {
    if (lodash.isEmpty(state.regions) || forceLoad) {
      const allRegions = await getAllRegions()
      commit(types.REGIONS, allRegions)
    }
  },
  async loadRegionalEnv ({state, commit}, regionEnv) {
    const envValues = await getRegionEnvsList(regionEnv)
    const regionalEnvState = buildBackEndRegionalEnvState(regionEnv, envValues)

    Object.entries(regionalEnvState).forEach(([envCode, envValue]) => {
      commit(types.BACKEND_REGIONAL_ENV_UPDATE, {envCode, envValue})
    })
  },
  updatePublicPathLastSelectedRegion ({state, commit}, region) {
    const result = { ...region }
    commit(types.PUBLIC_PATH_LAST_SELECTED_REGION, result)
  },
  setIsCrossRegionLogin ({state, commit}, isCrossRegionLogin) {
    commit(types.CROSS_REGION_LOGIN, isCrossRegionLogin)
  },
}

/**
 * State Mutations
 */
const mutations = {
  [types.RESET_STATE] (state) {
    state.currentRegion = lodash.cloneDeep(defaultState.currentRegion)
    state.regions = lodash.cloneDeep(defaultState.regions)
    state.backEndRegionalEnv = getDefaultBackEndRegionalEnv()
  },
  [types.CURRENT_REGION] (state, region) {
    state.currentRegion = region
  },
  [types.REGIONS] (state, regions) {
    const result = regions.reduce((rs, region) => {
      rs[region.name] = region
      return rs
    }, {})
    state.regions = result
  },
  [types.ACTIVE_REGIONS] (state, activeRegions) {
    const orderedRegions = lodash.orderBy(activeRegions, 'order', 'asc')
    state.activeRegions = orderedRegions.map(item => {
      return Object.assign({}, item, {
        get domain () {
          return item.domain.replace('/adLogin', '/api/adLogin')
        },
      })
    })
  },
  [types.PUBLIC_PATH_LAST_SELECTED_REGION] (state, publicPathLastSelectedRegion) {
    state.publicPathLastSelectedRegion = publicPathLastSelectedRegion
  },
  [types.BACKEND_REGIONAL_ENV_UPDATE] (state, { envCode, envValue }) {
    state.backEndRegionalEnv[envCode] = envValue
  },
  [types.CROSS_REGION_LOGIN] (state, isCrossRegionLogin) {
    state.isCrossRegionLogin = isCrossRegionLogin
  },
}

export default {
  namespaced: true,
  state: lodash.cloneDeep(defaultState),
  getters,
  actions,
  mutations,
}
